import React, { useEffect, useRef, useState } from 'react';
import { useVgsCollectContext } from '../../../context/VgsCollect';
import { createField } from '../../../services/vgs/fields';
import {
  ReplacePattern,
  VaultInputEventHandler,
  VaultInputType,
} from './types';
import { setFieldDefaults } from './fieldDefaults';
import { useVgsFieldContext } from '../../../context/VgsField';
import { fieldStyling } from '../../../services/vgs/vgsStyling';

const setEventValue = ({
  currentFieldValue,
  eventRef,
  eventHandler,
}: {
  currentFieldValue: boolean;
  eventRef: React.MutableRefObject<boolean>;
  eventHandler?: VaultInputEventHandler;
}) => {
  if (currentFieldValue !== eventRef.current) {
    if (eventHandler) {
      eventHandler(currentFieldValue);
    }
    eventRef.current = currentFieldValue;
  }
};

export default function VaultInput({
  name,
  type,
  placeholder,
  validations,
  style,
  maskInput,
  serializers,
  replacePatterns,
  onEmpty,
  onDirty,
  onValid,
  onFocused,
  onTouched,
}: {
  name: string;
  type: VaultInputType;
  placeholder?: string;
  validations?: RegExp[];
  style?: Style;
  maskInput?: MaskInput;
  serializers?: VgsSerializer[];
  replacePatterns?: ReplacePattern[];
  onEmpty?: VaultInputEventHandler;
  onDirty?: VaultInputEventHandler;
  onValid?: VaultInputEventHandler;
  onFocused?: VaultInputEventHandler;
  onTouched?: VaultInputEventHandler;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const {
    state: { form },
  } = useVgsCollectContext();
  const { dispatch } = useVgsFieldContext();
  const empty = useRef(true);
  const dirty = useRef(false);
  const valid = useRef(true);
  const focused = useRef(false);
  const touched = useRef(false);
  const [field, setField] = useState<VGSField>();

  useEffect(() => {
    // do no create field if
    if (
      // html element not present
      !elRef.current ||
      // form is not instantiated
      !form ||
      // the field has already been created
      form.fields?.some((f) => f.name === name)
    ) {
      return;
    }

    const {
      type: fieldType,
      placeholder: adjustedPlaceholder,
      maskInput: adjustedMaskInput,
      replacePatterns: adjustedReplacePatterns,
      validations: adjustedValidations,
    } = setFieldDefaults({
      type,
      placeholder,
      maskInput,
      validations,
      replacePatterns,
    });

    const field = createField({
      type: fieldType,
      serializers,
      form,
      elRef,
      name,
      placeholder: adjustedPlaceholder,
      validations: adjustedValidations,
      style: { ...fieldStyling, ...style },
      maskInput: adjustedMaskInput,
      replacePatterns: adjustedReplacePatterns,
    });
    setField(field);

    const fieldUpdateHandler = (fieldState: VGSFieldState) => {
      dispatch({ type: 'ADD_LOADED_FIELD', value: name });
      setEventValue({
        currentFieldValue: fieldState.isEmpty,
        eventRef: empty,
        eventHandler: onEmpty,
      });
      setEventValue({
        currentFieldValue: fieldState.isDirty,
        eventRef: dirty,
        eventHandler: onDirty,
      });
      setEventValue({
        currentFieldValue: fieldState.isTouched,
        eventRef: touched,
        eventHandler: onTouched,
      });
      setEventValue({
        currentFieldValue: fieldState.isFocused,
        eventRef: focused,
        eventHandler: onFocused,
      });
      setEventValue({
        currentFieldValue: fieldState.isValid,
        eventRef: valid,
        eventHandler: onValid,
      });
    };
    field.on('update', fieldUpdateHandler);
    () => {
      field.off('update', fieldUpdateHandler);
      field.delete();
    };
  }, [
    dirty,
    dispatch,
    empty,
    focused,
    form,
    maskInput,
    name,
    onDirty,
    onEmpty,
    onFocused,
    onTouched,
    onValid,
    placeholder,
    replacePatterns,
    serializers,
    style,
    touched,
    type,
    valid,
    validations,
  ]);
  useEffect(() => {
    dispatch({ type: 'ADD_FIELD', value: name });
    return () => dispatch({ type: 'REMOVE_FIELD', value: name });
  }, [dispatch, name]);
  useEffect(() => {
    field?.update({ css: { ...fieldStyling, ...style } });
  }, [field, style]);
  return <div className='vgs-ref' ref={elRef}></div>;
}
